import React, { useRef, useState, useEffect } from 'react';
import { initializeModal } from '../../../utils/ModalUtils';
import { processError } from '../../../services/ServiceUtils';
import { importStrategyPreview, importStrategy } from '../../../services/StrategiesService';

const CONFLICT_LABELS = {
    automation: 'Automação',
    orderTemplate: 'Order Template',
    withdrawTemplate: 'Withdraw Template'
};

const DEFAULT_STATE = {
    step: 'upload',   // 'upload' | 'preview' | 'importing'
    fileData: null,
    preview: null,
    previewError: '',
    resolutions: {},  // key: "type:name:symbol_or_coin" → { resolution, newName }
    importError: '',
    loading: false
};

/**
 * props:
 * - onSuccess(strategy)
 */
function ImportStrategyModal(props) {
    const fileInputRef = useRef(null);
    const btnClose = useRef(null);
    const [state, setState] = useState({ ...DEFAULT_STATE });

    useEffect(() => {
        initializeModal('modalImportStrategy', () => setState({ ...DEFAULT_STATE }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function conflictKey(c) {
        return `${c.type}:${c.name}:${c.symbol || c.coin || ''}`;
    }

    async function onFileChange(e) {
        const file = e.target.files[0];
        if (!file) return;
        setState(s => ({ ...s, loading: true, previewError: '', step: 'upload' }));

        try {
            const text = await file.text();
            const parsed = JSON.parse(text);

            const result = await importStrategyPreview(parsed);

            const initialResolutions = {};
            for (const c of (result.conflicts || [])) {
                initialResolutions[conflictKey(c)] = { resolution: 'rename', newName: c.suggestedName };
            }

            setState(s => ({
                ...s,
                loading: false,
                fileData: parsed,
                preview: result,
                resolutions: initialResolutions,
                previewError: '',
                step: 'preview'
            }));
        } catch (err) {
            let msg = processError(err);
            if (err instanceof SyntaxError) msg = 'Arquivo inválido. Selecione um arquivo .hydra.json exportado pelo Hydra.';
            setState(s => ({ ...s, loading: false, previewError: msg, step: 'upload', fileData: null, preview: null }));
        }

        // reset file input so same file can be re-selected
        e.target.value = '';
    }

    function setResolution(c, resolution) {
        const key = conflictKey(c);
        setState(s => ({
            ...s,
            resolutions: { ...s.resolutions, [key]: { ...s.resolutions[key], resolution } }
        }));
    }

    function setNewName(c, newName) {
        const key = conflictKey(c);
        setState(s => ({
            ...s,
            resolutions: { ...s.resolutions, [key]: { ...s.resolutions[key], newName } }
        }));
    }

    function buildResolutionsList() {
        const { preview, resolutions } = state;
        if (!preview) return [];
        return (preview.conflicts || []).map(c => {
            const key = conflictKey(c);
            const r = resolutions[key] || { resolution: 'rename', newName: c.suggestedName };
            return {
                type: c.type,
                name: c.name,
                symbol: c.symbol || undefined,
                coin: c.coin || undefined,
                resolution: r.resolution,
                newName: r.resolution === 'rename' ? (r.newName || c.suggestedName) : undefined
            };
        });
    }

    async function onImportClick() {
        setState(s => ({ ...s, loading: true, importError: '', step: 'importing' }));
        try {
            const resolutions = buildResolutionsList();
            const result = await importStrategy(state.fileData, resolutions);
            btnClose.current.click();
            if (props.onSuccess) props.onSuccess(result);
        } catch (err) {
            setState(s => ({ ...s, loading: false, importError: processError(err), step: 'preview' }));
        }
    }

    const { step, preview, previewError, resolutions, importError, loading } = state;
    const conflicts = preview ? (preview.conflicts || []) : [];
    const hasRenameWithEmptyName = conflicts.some(c => {
        const key = conflictKey(c);
        const r = resolutions[key] || {};
        return r.resolution === 'rename' && !r.newName?.trim();
    });

    return (
        <div className="modal fade" id="modalImportStrategy" tabIndex="-1" role="dialog" aria-labelledby="modalImportStrategyTitle" aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
                <div className="modal-content">
                    <div className="modal-header">
                        <p className="modal-title fw-semibold" id="modalImportStrategyTitle">Importar Estratégia</p>
                        <button ref={btnClose} type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Fechar" />
                    </div>

                    <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                        {/* Upload */}
                        <div className="mb-3">
                            <label className="form-label fw-semibold">Arquivo de estratégia</label>
                            <div className="d-flex align-items-center gap-2">
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".json,.hydra.json"
                                    className="d-none"
                                    onChange={onFileChange}
                                />
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary btn-sm"
                                    onClick={() => fileInputRef.current.click()}
                                    disabled={loading}
                                >
                                    {loading ? <span className="spinner-border spinner-border-sm me-1" /> : null}
                                    {step === 'upload' ? 'Selecionar arquivo...' : 'Trocar arquivo'}
                                </button>
                                {preview && (
                                    <span className="text-muted small">
                                        {preview.summary.strategyName} — {preview.summary.symbol}
                                    </span>
                                )}
                            </div>
                            <small className="text-muted">Selecione um arquivo <code>.hydra.json</code> exportado pelo Hydra.</small>
                            {previewError && <div className="alert alert-danger py-2 small mt-2">{previewError}</div>}
                        </div>

                        {/* Preview */}
                        {step !== 'upload' && preview && (
                            <>
                                <div className="card border-0 bg-light p-3 mb-3">
                                    <p className="fw-semibold mb-2 small">Resumo da estratégia importada</p>
                                    <div className="d-flex flex-wrap gap-3 small">
                                        <span><strong>Nome:</strong> {preview.summary.strategyName}</span>
                                        <span><strong>Símbolo:</strong> {preview.summary.symbol}</span>
                                        <span><strong>Monitor:</strong> {preview.summary.monitorType}{preview.summary.monitorInterval ? ` ${preview.summary.monitorInterval}` : ''}</span>
                                        <span><strong>Automações:</strong> {preview.summary.automationsCount}</span>
                                    </div>
                                    {preview.summary.hasTargetAutomationActions && (
                                        <div className="alert alert-warning py-1 small mt-2 mb-0">
                                            ⚠ Esta estratégia contém ações que referenciam outras automações. Essas referências não são portáveis e precisarão ser reconfiguradas manualmente após a importação.
                                        </div>
                                    )}
                                </div>

                                {/* Conflicts */}
                                {conflicts.length === 0 ? (
                                    <div className="alert alert-success py-2 small">
                                        Nenhum conflito detectado. Todos os componentes serão criados como novos.
                                    </div>
                                ) : (
                                    <>
                                        <p className="fw-semibold mb-2">
                                            {conflicts.length} conflito{conflicts.length > 1 ? 's' : ''} encontrado{conflicts.length > 1 ? 's' : ''}
                                        </p>
                                        <p className="text-muted small mb-3">
                                            Para cada item, escolha se deseja sobrescrever o existente ou criar com um novo nome.
                                        </p>
                                        {conflicts.map((c, i) => {
                                            const key = conflictKey(c);
                                            const r = resolutions[key] || { resolution: 'rename', newName: c.suggestedName };
                                            return (
                                                <div key={i} className="card mb-2 border-warning">
                                                    <div className="card-body py-2 px-3">
                                                        <div className="d-flex align-items-start gap-2 flex-wrap">
                                                            <span className="badge bg-warning text-dark">{CONFLICT_LABELS[c.type] || c.type}</span>
                                                            <div className="flex-grow-1">
                                                                <p className="mb-1 small fw-semibold">{c.name}</p>
                                                                {c.parentAutomation && (
                                                                    <p className="mb-1 text-muted small">Pertence à automação: {c.parentAutomation}</p>
                                                                )}
                                                                <div className="d-flex gap-3 flex-wrap mt-1">
                                                                    <div className="form-check mb-0">
                                                                        <input
                                                                            type="radio"
                                                                            className="form-check-input"
                                                                            id={`res-ow-${i}`}
                                                                            checked={r.resolution === 'overwrite'}
                                                                            onChange={() => setResolution(c, 'overwrite')}
                                                                        />
                                                                        <label className="form-check-label small" htmlFor={`res-ow-${i}`}>
                                                                            Sobrescrever existente
                                                                        </label>
                                                                    </div>
                                                                    <div className="form-check mb-0">
                                                                        <input
                                                                            type="radio"
                                                                            className="form-check-input"
                                                                            id={`res-rn-${i}`}
                                                                            checked={r.resolution === 'rename'}
                                                                            onChange={() => setResolution(c, 'rename')}
                                                                        />
                                                                        <label className="form-check-label small" htmlFor={`res-rn-${i}`}>
                                                                            Criar com novo nome:
                                                                        </label>
                                                                    </div>
                                                                    {r.resolution === 'rename' && (
                                                                        <input
                                                                            type="text"
                                                                            className="form-control form-control-sm"
                                                                            style={{ maxWidth: 280 }}
                                                                            value={r.newName || ''}
                                                                            onChange={e => setNewName(c, e.target.value)}
                                                                            placeholder={c.suggestedName}
                                                                        />
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </>
                                )}

                                {importError && <div className="alert alert-danger py-2 small mt-2">{importError}</div>}
                            </>
                        )}
                    </div>

                    <div className="modal-footer">
                        <button type="button" className="btn btn-sm btn-secondary" data-bs-dismiss="modal">Cancelar</button>
                        {step !== 'upload' && preview && (
                            <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                onClick={onImportClick}
                                disabled={loading || hasRenameWithEmptyName}
                            >
                                {loading
                                    ? <><span className="spinner-border spinner-border-sm me-1" />Importando...</>
                                    : 'Importar'}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ImportStrategyModal;
